import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import ImagenConFallback from '../components/ImagenConFallback'
import Revelar from '../components/Revelar'
// Reutiliza dos íconos genéricos ya escritos para la wiki de /educacion (X y
// chevron) en vez de duplicar el SVG — el chevron rotado hace de flecha
// izquierda/derecha del lightbox.
import { IconoChevronDown, IconoX } from '../components/IconosWiki'
import { useGaleria } from '../hooks/useGaleria'

function IconoAmpliar({ className = 'size-6' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
      <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
      <path d="M3 16v3a2 2 0 0 0 2 2h3" />
      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  )
}

function Esqueleto() {
  return <div className="aspect-square animate-pulse rounded-sm bg-carbon" />
}

function Lightbox({ imagenes, indice, onCambiar, onCerrar }) {
  const sinMovimiento = useReducedMotion()
  const total = imagenes.length
  const actual = imagenes[indice]

  useEffect(() => {
    function alPulsarTecla(evento) {
      if (evento.key === 'Escape') onCerrar()
      if (evento.key === 'ArrowRight') onCambiar((indice + 1) % total)
      if (evento.key === 'ArrowLeft') onCambiar((indice - 1 + total) % total)
    }
    const overflowPrevio = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', alPulsarTecla)
    return () => {
      document.body.style.overflow = overflowPrevio
      document.removeEventListener('keydown', alPulsarTecla)
    }
  }, [indice, total, onCambiar, onCerrar])

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label="Foto ampliada"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-10"
      initial={sinMovimiento ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={sinMovimiento ? undefined : { opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div
        aria-hidden="true"
        onClick={onCerrar}
        className="absolute inset-0 bg-noche/95 backdrop-blur-sm"
      />

      <button
        type="button"
        onClick={onCerrar}
        aria-label="Cerrar"
        className="area-tactil absolute top-4 right-4 z-10 -m-2 rounded-full p-2 text-marfil-suave transition-colors hover:text-oro-claro sm:top-6 sm:right-6"
      >
        <IconoX className="size-6" />
      </button>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={() => onCambiar((indice - 1 + total) % total)}
            aria-label="Foto anterior"
            className="area-tactil absolute left-2 z-10 -m-2 rounded-full p-2 text-marfil-suave transition-colors hover:text-oro-claro sm:left-6"
          >
            <IconoChevronDown className="size-6 rotate-90" />
          </button>
          <button
            type="button"
            onClick={() => onCambiar((indice + 1) % total)}
            aria-label="Foto siguiente"
            className="area-tactil absolute right-2 z-10 -m-2 rounded-full p-2 text-marfil-suave transition-colors hover:text-oro-claro sm:right-6"
          >
            <IconoChevronDown className="size-6 -rotate-90" />
          </button>
        </>
      )}

      <figure className="relative z-10 flex max-h-full max-w-full flex-col items-center">
        <AnimatePresence mode="wait">
          <motion.img
            key={actual.url}
            src={actual.url}
            alt={actual.alt}
            initial={sinMovimiento ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={sinMovimiento ? undefined : { opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="max-h-[75svh] max-w-full rounded-sm object-contain"
          />
        </AnimatePresence>
        {actual.alt && (
          <figcaption className="mt-4 max-w-lg text-center text-sm text-marfil-suave/80">
            {actual.alt}
          </figcaption>
        )}
      </figure>
    </motion.div>
  )
}

/**
 * Galería de fotos del criadero en la portada: grid con zoom al pasar el
 * mouse y lightbox con navegación por teclado. Se administra desde /admin
 * (pestaña "Galería"); si todavía no hay fotos cargadas, la sección no se
 * renderiza — nada de placeholders vacíos en el sitio en vivo.
 */
function Galeria() {
  const { imagenes, cargando } = useGaleria()
  const [indiceAbierto, setIndiceAbierto] = useState(null)
  const sinMovimiento = useReducedMotion()

  if (!cargando && imagenes.length === 0) return null

  return (
    <section id="galeria" aria-labelledby="titulo-galeria" className="seccion-y border-t border-oro-profundo/20">
      <div className="contenedor">
        <Revelar>
          <p className="text-[0.7rem] uppercase tracking-[0.3em] text-oro sm:text-xs sm:tracking-[0.35em]">
            Galería
          </p>
          <h2 id="titulo-galeria" className="mt-4 text-seccion font-light">
            Momentos del criadero
          </h2>
          <p className="mt-4 max-w-xl text-entradilla text-marfil-suave/80">
            Un vistazo real a nuestros gaticos y a cómo cuidamos cada camada.
          </p>
        </Revelar>

        <div className="mt-10 grid grid-cols-2 gap-3 sm:mt-12 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
          {cargando
            ? Array.from({ length: 8 }, (_, i) => <Esqueleto key={i} />)
            : imagenes.map((imagen, i) => (
                <motion.button
                  key={`${imagen.url}-${i}`}
                  type="button"
                  onClick={() => setIndiceAbierto(i)}
                  initial={sinMovimiento ? false : { opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: Math.min(i, 7) * 0.05, ease: 'easeOut' }}
                  className="group relative aspect-square overflow-hidden rounded-sm bg-carbon"
                  aria-label={`Ver foto ampliada${imagen.alt ? `: ${imagen.alt}` : ''}`}
                >
                  <ImagenConFallback
                    src={imagen.url}
                    alt={imagen.alt}
                    className="size-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                  />
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 flex items-center justify-center bg-noche/0 opacity-0 transition-all duration-300 group-hover:bg-noche/40 group-hover:opacity-100"
                  >
                    <IconoAmpliar className="size-6 text-marfil drop-shadow-lg" />
                  </div>
                </motion.button>
              ))}
        </div>
      </div>

      <AnimatePresence>
        {indiceAbierto !== null && (
          <Lightbox
            key="lightbox"
            imagenes={imagenes}
            indice={indiceAbierto}
            onCambiar={setIndiceAbierto}
            onCerrar={() => setIndiceAbierto(null)}
          />
        )}
      </AnimatePresence>
    </section>
  )
}

export default Galeria
